import React, { FC, useEffect, useRef, useState } from 'react';
import { EditNewProjectModalProps } from './EditNewProjectModalProps';
import useTranslation from '../../hooks/useTranslation';
import CustomModal from '../../components/CustomModal';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import ProjectForm from '../../components/ProjectForm';
import NewUnitTabs from '../../components/NewUnitTabs';
import { useTypedSelector } from '../../store';
import { selectResponsibleOptionsList } from '../../store/users/selectors';
import { getBusinessUnits } from '../../store/selectData/selectors';
import { selectConfidentialPermission } from '../../store/userData/selectors';
import {
  returnNewUnitValidationSchema,
  calculateTabErrors,
} from '../CreateNewUnitModal/utils';
import { useDispatch } from 'react-redux';
import {
  fetchSiteLocation,
  updateProject,
} from '../../store/projects/actionWrapper';
import { selectSiteLocation } from '../../store/projects/selectors';
import { modalsActions } from '../../store/modals/slice';
import { ProjectFormRef } from '../../components/ProjectForm/ProjectForm';

const EditNewProjectModal: FC<EditNewProjectModalProps> = ({
  projectsList,
  project,
  closeHandler,
  establishmentType,
  countries,
  setActiveProject,
  disableRestrictions,
  additionalAction,
  readonly,
  submitTrigger,
  directiveOpen: initialDirectiveOpen,
}) => {
  const dispatch = useDispatch();
  const editCloseProjectHandler = (data) => {
    submitTrigger();
    dispatch(
      updateProject({
        project: {
          ...data,
          establishmentType: ESTABLISHMENT_TYPE.NEW_UNIT,
        },
        action: additionalAction,
      }),
    );
    setFormModified(false);
    closeHandler();
  };
  const [statusUpdateConfirmationModal, setStatusUpdateConfiramtionModal] =
    useState(false);
  const [directiveOpen, setDirectiveOpen] = useState(initialDirectiveOpen);
  useEffect(() => {
    setDirectiveOpen(initialDirectiveOpen);
  }, [initialDirectiveOpen, establishmentType]);
  const formRef = useRef<ProjectFormRef>(null);
  const t = useTranslation();
  const isConfidentialAllowed = useTypedSelector(selectConfidentialPermission);
  const usersList = useTypedSelector(selectResponsibleOptionsList);
  const BuList = useTypedSelector(getBusinessUnits);
  const selectedSiteLocation = useTypedSelector(selectSiteLocation);
  const validationSchema = returnNewUnitValidationSchema(t, project?.isep);
  const [formModified, setFormModified] = React.useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = React.useState(
    project?.countryCode || '',
  );

  if (!project) return <></>;
  const onCountryChange = (country) => {
    setSelectedCountry(country.target.value);
  };

  const onBUChange = (bu) => {
    dispatch(fetchSiteLocation(bu.businessUnitId || ''));
  };

  const BuFiltered = BuList.filter(
    (bu) => bu.countryId === (selectedCountry || project?.countryCode),
  );
  const openStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(true);
  };
  const closeStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(false);
  };

  const updateStatusHandler = (id) => {
    setActiveProject(id);
    dispatch(modalsActions.openProjectStatusModal());
  };

  const updateStatusClicked = (id: string) => {
    if (formModified) {
      openStatusConfirmationModal();
      return;
    }
    updateStatusHandler(id);
  };

  const proceedWithStatusChange = () => {
    formRef.current.submit();
    closeStatusConfirmationModal();
    updateStatusHandler(project.id);
  };

  return (
    <CustomModal
      isVisible={establishmentType === ESTABLISHMENT_TYPE.NEW_UNIT}
      onClose={closeHandler}
      formModified={formModified}
      title={
        directiveOpen ? t('labels.projectDirective') : t('common.editProject')
      }
      onBackBtnClick={directiveOpen ? () => setDirectiveOpen(false) : undefined}
      useConfirmation
      statusConfirmationModal={statusUpdateConfirmationModal}
      closeStatusConfirmationModal={closeStatusConfirmationModal}
      proceedStatusUpdate={proceedWithStatusChange}
      children={
        <ProjectForm
          onSubmit={editCloseProjectHandler}
          ref={formRef}
          directiveOpen={directiveOpen}
          onDirectiveBack={() => setDirectiveOpen(false)}
          setFormModified={(isDirty) => setFormModified(isDirty)}
          project={project}
          onClose={closeHandler}
          submitBtnTitle={t('common.update')}
          projectType={project?.establishmentType}
          selectedSiteLocation={selectedSiteLocation}
          validationSchema={validationSchema}
          isEdit
          setFormErrors={setFormErrors}
          disableRestrictions={disableRestrictions}
          readOnly={readonly}
        >
          <NewUnitTabs
            projectsList={projectsList}
            currentProject={project}
            countries={countries}
            setActiveProject={updateStatusClicked}
            usersList={usersList}
            BuList={BuFiltered}
            tabErrors={calculateTabErrors(formErrors, true)}
            onCountryChange={onCountryChange}
            onBUChange={onBUChange}
            isConfidentialAllowed={isConfidentialAllowed}
            isEdit
            directiveOpen={directiveOpen}
            onShowDirective={() => setDirectiveOpen(true)}
            onHideDirective={() => setDirectiveOpen(false)}
          />
        </ProjectForm>
      }
    />
  );
};

export default EditNewProjectModal;
